import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import InventoryService from "../../../services/InventoryService";
import { useInventoryStore } from "../../../store/useInventoryStore";
import { INVENTORY_QUERY_KEYS } from "../useInventoryQueries";

const extractErrorMessage = (error, fallback = "خطا در ذخیره‌سازی موجودی") => {
  const data = error?.response?.data;

  if (typeof data?.message === "string") return data.message;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.error === "string") return data.error;

  if (data?.message && typeof data.message === "object") {
    return data.message.message || fallback;
  }

  if (typeof error?.message === "string") return error.message;

  return fallback;
};

const normalizeNumberOrNull = (val) => {
  if (val === "" || val === null || val === undefined) return null;
  const num = Number(val);
  return Number.isNaN(num) ? null : num;
};

const buildSavePayload = ({ warehouseId, productId, payload }) => ({
  warehouseId,
  productId,
  price: normalizeNumberOrNull(payload?.price),
  stock: normalizeNumberOrNull(payload?.stock),
  discount_percent: normalizeNumberOrNull(payload?.discount_percent),
  min_order: normalizeNumberOrNull(payload?.min_order) ?? 1,
  max_order: normalizeNumberOrNull(payload?.max_order),
  order_multiple: normalizeNumberOrNull(payload?.order_multiple) ?? 1,
  is_active:
    payload?.is_active !== undefined
      ? Boolean(payload.is_active)
      : true,
});

export const useSaveProductInventory = () => {
  const queryClient = useQueryClient();

  const setRowSaving = useInventoryStore((state) => state.setRowSaving);
  const clearRowSaving = useInventoryStore((state) => state.clearRowSaving);
  const markRowSaved = useInventoryStore((state) => state.markRowSaved);

  return useMutation({
    mutationKey: ["save-product-inventory"],

    mutationFn: async (variables) => {
      const { warehouseId, productId, payload } = variables || {};

      if (!warehouseId) {
        throw new Error("ابتدا انبار را انتخاب کنید.");
      }

      if (!productId) {
        throw new Error("شناسه محصول نامعتبر است.");
      }

      const finalPayload = buildSavePayload({
        warehouseId,
        productId,
        payload,
      });

      const response = await InventoryService.saveProductInventory(finalPayload);

      return {
        response,
        requestData: variables,
        finalPayload,
      };
    },

    onMutate: async (variables) => {
      const { productId, payload } = variables || {};

      if (productId) {
        setRowSaving(productId, true);
      }

      await queryClient.cancelQueries({
        queryKey: [...INVENTORY_QUERY_KEYS.all, "warehouse-products"],
      });

      const previousQueries = queryClient.getQueriesData({
        queryKey: [...INVENTORY_QUERY_KEYS.all, "warehouse-products"],
      });

      previousQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // حالت paginated
        if (Array.isArray(oldData?.results)) {
          queryClient.setQueryData(queryKey, {
            ...oldData,
            results: oldData.results.map((item) => {
              if (item.id !== productId) return item;
              return {
                ...item,
                ...payload,
              };
            }),
          });
          return;
        }

        // حالت array ساده
        if (Array.isArray(oldData)) {
          queryClient.setQueryData(
            queryKey,
            oldData.map((item) => {
              if (item.id !== productId) return item;
              return {
                ...item,
                ...payload,
              };
            })
          );
        }
      });

      return { previousQueries, productId };
    },

    onSuccess: ({ response, requestData, finalPayload }) => {
      const { productId, payload } = requestData;

      const updatedFields = {
        price: response?.price ?? finalPayload.price ?? payload?.price ?? "",
        stock: response?.stock ?? finalPayload.stock ?? payload?.stock ?? "",
        discount_percent:
          response?.discount_percent ??
          finalPayload.discount_percent ??
          payload?.discount_percent ??
          "",
        min_order:
          response?.min_order ??
          finalPayload.min_order ??
          payload?.min_order ??
          1,
        max_order:
          response?.max_order ??
          finalPayload.max_order ??
          payload?.max_order ??
          "",
        order_multiple:
          response?.order_multiple ??
          finalPayload.order_multiple ??
          payload?.order_multiple ??
          1,
        inventory_is_active:
          response?.inventory_is_active ??
          finalPayload.is_active ??
          payload?.is_active ??
          true,
      };

      markRowSaved(productId, updatedFields);

      const matchingQueries = queryClient.getQueriesData({
        queryKey: [...INVENTORY_QUERY_KEYS.all, "warehouse-products"],
      });

      matchingQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        if (Array.isArray(oldData?.results)) {
          queryClient.setQueryData(queryKey, {
            ...oldData,
            results: oldData.results.map((item) => {
              if (item.id !== productId) return item;

              return {
                ...item,
                ...updatedFields,
                inventory_id: response?.id ?? item.inventory_id,
              };
            }),
          });
          return;
        }

        if (Array.isArray(oldData)) {
          queryClient.setQueryData(
            queryKey,
            oldData.map((item) => {
              if (item.id !== productId) return item;

              return {
                ...item,
                ...updatedFields,
                inventory_id: response?.id ?? item.inventory_id,
              };
            })
          );
        }
      });

      toast.success("موجودی با موفقیت ویرایش شد.");
    },

    onError: (error, _variables, context) => {
      if (context?.previousQueries?.length) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error(extractErrorMessage(error));
    },

    onSettled: (_data, _error, variables, context) => {
      const rowId = variables?.productId || context?.productId;

      if (rowId) {
        clearRowSaving(rowId);
      }

      queryClient.invalidateQueries({
        queryKey: [...INVENTORY_QUERY_KEYS.all, "warehouse-products"],
      });
    },
  });
};
